import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Chip,
  Divider,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArticleIcon from '@mui/icons-material/Article';
import QuizIcon from '@mui/icons-material/Quiz';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import { useKnowledgebase } from '../../Contexts/KnowledgebaseContext';
import { useAuth } from '../../Contexts/AuthContext';
import { searchContent } from '../../Helpers/helpApi';

const CATEGORIES = [
  { label: 'Getting Started', value: 'getting-started' },
  { label: 'Excel Import', value: 'excel-import' },
  { label: 'Bridge Columns', value: 'bridge-columns' },
  { label: 'Pro Forma Statements', value: 'pro-forma' },
  { label: 'Variance Analysis', value: 'variance-analysis' },
  { label: 'Stat Drivers', value: 'stat-drivers' },
  { label: 'Data Export', value: 'data-export' },
  { label: 'Account Settings', value: 'account-settings' },
];

const CONTENT_TYPE_ICONS = {
  article: <ArticleIcon fontSize="small" sx={{ color: 'primary.main' }} />,
  faq: <QuizIcon fontSize="small" sx={{ color: 'secondary.main' }} />,
  video: <PlayCircleIcon fontSize="small" sx={{ color: 'success.main' }} />,
};

const CONTENT_TYPE_LABELS = {
  article: 'Article',
  faq: 'FAQ',
  video: 'Video',
};

export default function SearchView({ searchInputRef }) {
  const { searchQuery, setSearchQuery, searchResults, setSearchResults, isSearching, setIsSearching, openArticle, setActiveView } = useKnowledgebase();
  const auth = useAuth();
  const [searchError, setSearchError] = useState(null);
  const debounceRef = useRef(null);

  // Debounced search — fires 300ms after the user stops typing
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!searchQuery || searchQuery.trim().length < 3) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    setSearchError(null);

    debounceRef.current = setTimeout(async () => {
      try {
        const response = await searchContent(searchQuery.trim(), auth.activeUser?.accessToken);
        if (response?.data) {
          setSearchResults(Array.isArray(response.data) ? response.data : []);
        } else {
          setSearchResults([]);
        }
      } catch (err) {
        setSearchError('Search failed. Please try again.');
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchQuery, auth.activeUser?.accessToken, setSearchResults, setIsSearching]);

  const handleResultClick = (result) => {
    if (result.content_type === 'article') {
      openArticle(result.id);
    } else if (result.content_type === 'faq') {
      setActiveView('faq');
    } else if (result.content_type === 'video') {
      setActiveView('videos');
    }
  };

  const handleCategoryClick = (category) => {
    setSearchQuery(category.label);
  };

  const showBrowse = !searchQuery || searchQuery.trim().length < 3;
  const showNoResults = !isSearching && searchQuery.trim().length >= 3 && searchResults.length === 0 && !searchError;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Search input */}
      <Box sx={{ px: 2, pt: 2, pb: 1 }}>
        <TextField
          inputRef={searchInputRef}
          fullWidth
          size="small"
          placeholder="Search help articles, FAQs, videos..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {isSearching ? <CircularProgress size={16} /> : <SearchIcon fontSize="small" />}
              </InputAdornment>
            ),
          }}
          inputProps={{ 'aria-label': 'Search help content' }}
        />
      </Box>

      {/* Error state */}
      {searchError && (
        <Box sx={{ px: 2, pb: 1 }}>
          <Alert
            severity="error"
            action={
              <Typography
                variant="caption"
                sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => { setSearchError(null); setSearchQuery(searchQuery); }}
              >
                Try again
              </Typography>
            }
          >
            {searchError}
          </Alert>
        </Box>
      )}

      {/* No results */}
      {showNoResults && (
        <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            No results found for "{searchQuery}"
          </Typography>
          <Typography
            variant="body2"
            color="primary"
            sx={{ cursor: 'pointer', textDecoration: 'underline' }}
            component="a"
            href="mailto:support@bridgefirstforecasting.com"
          >
            Contact Support
          </Typography>
        </Box>
      )}

      {/* Search results */}
      {!showBrowse && searchResults.length > 0 && (
        <List dense sx={{ px: 1, flex: 1, overflowY: 'auto' }}>
          {searchResults.map((result, idx) => (
            <ListItemButton
              key={`${result.content_type}-${result.id}-${idx}`}
              onClick={() => handleResultClick(result)}
              sx={{ borderRadius: 1, mb: 0.5 }}
            >
              <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}>
                {CONTENT_TYPE_ICONS[result.content_type]}
              </Box>
              <ListItemText
                primary={result.title}
                secondary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
                    <Chip
                      label={CONTENT_TYPE_LABELS[result.content_type]}
                      size="small"
                      variant="outlined"
                      sx={{ height: 18, fontSize: '0.65rem' }}
                    />
                    {result.category && (
                      <Typography variant="caption" color="text.secondary">
                        {result.category}
                      </Typography>
                    )}
                  </Box>
                }
                primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
              />
            </ListItemButton>
          ))}
        </List>
      )}

      {/* Category browse (shown when query is empty or < 3 chars) */}
      {showBrowse && (
        <Box sx={{ px: 2, pt: 1 }}>
          <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Browse by topic
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {CATEGORIES.map((cat) => (
              <Chip
                key={cat.value}
                label={cat.label}
                onClick={() => handleCategoryClick(cat)}
                variant="outlined"
                size="small"
                clickable
              />
            ))}
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Quick nav links */}
          <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Quick access
          </Typography>
          <List dense>
            {[
              { label: 'FAQ', view: 'faq', icon: <QuizIcon fontSize="small" /> },
              { label: 'Video Tutorials', view: 'videos', icon: <PlayCircleIcon fontSize="small" /> },
              { label: "What's New", view: 'release-notes', icon: <ArticleIcon fontSize="small" /> },
            ].map((item) => (
              <ListItemButton
                key={item.view}
                onClick={() => setActiveView(item.view)}
                sx={{ borderRadius: 1, mb: 0.25 }}
              >
                <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                  {item.icon}
                </Box>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItemButton>
            ))}
          </List>
        </Box>
      )}
    </Box>
  );
}
